class MixAndMatch extends HTMLElement {
  constructor() {
    super();
    this.items = new Map();
    this.tiers = [];
    this.busy = false;
  }

  connectedCallback() {
    const config = this.querySelector('[data-mix-match-config]');
    this.settings = config ? JSON.parse(config.textContent) : {};
    this.tiers = (this.settings.tiers || []).slice().sort((a, b) => a.min - b.min);
    this.moneyFormat = this.settings.moneyFormat || '${{amount}}';

    this.shell = this.querySelector('[data-ref="mobile-shell"]');
    this.refs = {
      grid: this.querySelector('[data-ref="grid"]'),
      desktopItems: this.querySelector('[data-ref="desktop-items-list"]'),
      mobileItems: this.querySelector('[data-ref="mobile-items-list"]'),
      desktopEmpty: this.querySelector('[data-ref="desktop-empty"]'),
      count: this.querySelectorAll('[data-ref="pack-count"]'),
      progressMessage: this.querySelectorAll('[data-ref="tier-progress-message"]'),
      progressSegments: this.querySelectorAll('[data-ref="tier-progress-segments"]'),
      progressRoot: this.querySelectorAll('[data-ref="tier-progress"]'),
      nextTier: this.querySelector('[data-ref="next-tier"]'),
      totals: this.querySelector('[data-ref="totals"]'),
      subtotal: this.querySelector('[data-ref="subtotal"]'),
      discountRow: this.querySelector('[data-ref="discount-row"]'),
      discountValue: this.querySelector('[data-ref="discount-value"]'),
      atcButtons: this.querySelectorAll('[data-ref="add-to-cart-button"]'),
      atcLabels: this.querySelectorAll('[data-ref="atc-label"]'),
      toggle: this.querySelector('[data-ref="mobile-toggle-button"]'),
      errors: this.querySelectorAll('[data-ref="error-message"]'),
    };

    // Les cartes ne bougent plus apres le montage : on indexe leurs noeuds
    // une fois, plutot que de re-interroger le DOM a chaque rendu.
    this.cardRefs = new Map();
    this.querySelectorAll('[data-mix-match-card]').forEach((card) => {
      this.cardRefs.set(card.dataset.mixMatchCard, {
        card,
        button: card.querySelector('[data-mix-match-add]'),
        stepper: card.querySelector('[data-ref="card-qty"]'),
        badge: card.querySelector('[data-ref="card-badge"]'),
        input: card.querySelector('[data-mix-match-qty-input]'),
        soldOut: card.classList.contains('mix-match-card--sold-out'),
      });
    });
    this.unitPriceNodes = this.querySelectorAll('[data-ref="unit-price"]');

    this.watchCartMutations();

    // Retour arriere : ne vider que si le pack est reellement parti au panier.
    // Un aller-retour vers une fiche produit doit le laisser intact.
    window.addEventListener('pageshow', (event) => {
      if (!event.persisted) return;
      if (this.readHandoff()) {
        this.clearHandoff();
        this.clearPack();
      } else {
        this.setLoading(false);
        this.busy = false;
      }
    });

    // Le pack differe est ecrit avant que la page ne parte
    window.addEventListener('pagehide', () => this.savePack());

    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('change', this.onChange.bind(this));

    this.restorePack();
    this.render();
  }

  /* ---------- events ---------- */

  onClick(event) {
    const addButton = event.target.closest('[data-mix-match-add]');
    if (addButton) {
      event.preventDefault();
      this.addItem(addButton.dataset.mixMatchAdd);
      return;
    }

    const removeButton = event.target.closest('[data-mix-match-remove]');
    if (removeButton) {
      event.preventDefault();
      this.setQuantity(removeButton.dataset.mixMatchRemove, 0);
      return;
    }

    const stepButton = event.target.closest('[data-mix-match-step]');
    if (stepButton) {
      event.preventDefault();
      const id = stepButton.dataset.variantId;
      const current = this.items.get(id)?.quantity || 0;
      this.setQuantity(id, current + Number(stepButton.dataset.mixMatchStep));
      return;
    }

    if (event.target.closest('[data-ref="add-to-cart-button"]')) {
      event.preventDefault();
      this.addToCart();
      return;
    }

    if (event.target.closest('[data-ref="mobile-toggle-button"]')) {
      event.preventDefault();
      this.toggleMobilePanel();
      return;
    }

    if (event.target.closest('[data-ref="mobile-close-button"]')) {
      event.preventDefault();
      this.toggleMobilePanel(false);
    }
  }

  onChange(event) {
    const input = event.target.closest('[data-mix-match-qty-input]');
    if (!input) return;
    this.setQuantity(input.dataset.variantId, parseInt(input.value, 10) || 0);
  }

  /* ---------- state ---------- */

  addItem(variantId) {
    const card = this.querySelector(`[data-mix-match-card="${variantId}"]`);
    if (!card) return;

    const existing = this.items.get(variantId);
    if (existing) {
      existing.quantity += 1;
    } else {
      this.items.set(variantId, {
        quantity: 1,
        title: card.dataset.productTitle,
        price: Number(card.dataset.price),
        compareAt: Number(card.dataset.compareAt) || 0,
        image: card.dataset.image || '',
      });
    }
    this.render();
  }

  setQuantity(variantId, quantity) {
    if (quantity <= 0) {
      this.items.delete(variantId);
    } else {
      const item = this.items.get(variantId);
      if (!item) return;
      item.quantity = quantity;
    }
    this.render();
  }

  /* ---------- resynchronisation du code de palier ---------- */

  // Un code pose sur le panier est fige : passer de 5 a 4 articles invalide
  // le code du palier 5 et fait tomber la remise a zero. On surveille donc
  // les modifications du panier pour reappliquer le code du bon palier.
  watchCartMutations() {
    if (!this.tiers.some((tier) => tier.code)) return;

    const CART_MUTATIONS = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;
    const originalFetch = window.fetch;
    const self = this;

    window.fetch = function patchedFetch(resource, init) {
      const url = typeof resource === 'string' ? resource : (resource && resource.url) || '';
      const result = originalFetch.apply(this, arguments);

      if (!self.syncing && CART_MUTATIONS.test(url)) {
        result
          .then((response) => response.clone().json())
          .then((cart) => self.syncTierDiscount(cart))
          .catch(() => {});
      }

      return result;
    };
  }

  async syncTierDiscount(cartFromResponse) {
    if (this.syncing) return;

    const cart =
      cartFromResponse && Array.isArray(cartFromResponse.items)
        ? cartFromResponse
        : await fetch(`${this.settings.routes.cart}.js`).then((r) => r.json());

    // Seules les lignes posees par le builder comptent dans le palier
    const packQuantity = cart.items.reduce((total, item) => {
      const marked = item.properties && item.properties._pack;
      return marked ? total + item.quantity : total;
    }, 0);

    const targetTier = this.currentTier(packQuantity);
    const targetCode = targetTier && targetTier.code ? targetTier.code : '';
    if (!targetCode || targetCode === this.appliedCode) return;

    this.syncing = true;
    // Le panier affiche les prix pleins tant que le nouveau code n'est pas pose :
    // on voile le montant plutot que de laisser lire un prix qui va changer.
    document.documentElement.classList.add('mm-cart-syncing');

    try {
      await fetch(`/discount/${encodeURIComponent(targetCode)}`, { credentials: 'same-origin' });
      this.appliedCode = targetCode;

      // Le panier affiche doit refleter le nouveau montant
      const refreshed = await fetch(`${this.settings.routes.cart}/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          updates: {},
          sections: 'cart-drawer,cart-icon-bubble',
          sections_url: window.location.pathname,
        }),
      }).then((r) => r.json());

      this.renderCartSections(refreshed.sections);
    } catch (error) {
      // Le panier reste correct cote Shopify, seul le palier n'a pas suivi
    } finally {
      this.syncing = false;
      document.documentElement.classList.remove('mm-cart-syncing');
    }
  }

  /* ---------- persistance ---------- */

  get storageKey() {
    // Une cle par page : deux variantes de test ne partagent pas le meme pack
    return `mix-and-match:${this.dataset.sectionId}:${window.location.pathname}`;
  }

  // localStorage est synchrone : on n'ecrit pas a chaque pression sur + ou -
  schedulePackSave() {
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.savePack(), 400);
  }

  savePack() {
    clearTimeout(this.saveTimer);
    try {
      const items = Array.from(this.items.entries()).map(([id, item]) => ({
        id,
        quantity: item.quantity,
        title: item.title,
        price: item.price,
        compareAt: item.compareAt,
        image: item.image,
      }));

      if (!items.length) {
        localStorage.removeItem(this.storageKey);
        return;
      }

      localStorage.setItem(this.storageKey, JSON.stringify({ savedAt: Date.now(), items }));
    } catch (error) {
      // Navigation privee ou stockage plein : le pack reste en memoire, sans plus
    }
  }

  restorePack() {
    let stored;
    try {
      stored = localStorage.getItem(this.storageKey);
      if (!stored) return;
    } catch (error) {
      return;
    }

    try {
      const data = JSON.parse(stored);
      const maxAge = (this.settings.packMaxAgeDays || 7) * 24 * 60 * 60 * 1000;

      if (!data || !Array.isArray(data.items) || Date.now() - (data.savedAt || 0) > maxAge) {
        localStorage.removeItem(this.storageKey);
        return;
      }

      data.items.forEach((entry) => {
        const id = String(entry.id || '');
        const quantity = Math.min(Math.max(parseInt(entry.quantity, 10) || 0, 0), 99);
        if (!id || !quantity) return;

        const card = this.querySelector(`[data-mix-match-card="${id}"]`);

        // Carte presente : on prend ses valeurs, donc le prix du jour.
        // Carte absente, cas d'une autre page de la grille : on reprend
        // l'instantane stocke, sans quoi le pack se viderait en paginant.
        if (card) {
          if (card.classList.contains('mix-match-card--sold-out')) return;
          this.items.set(id, {
            quantity,
            title: card.dataset.productTitle,
            price: Number(card.dataset.price),
            compareAt: Number(card.dataset.compareAt) || 0,
            image: card.dataset.image || '',
          });
          return;
        }

        const price = Number(entry.price);
        if (!(price > 0) || !entry.title) return;

        this.items.set(id, {
          quantity,
          title: entry.title,
          price,
          compareAt: Number(entry.compareAt) || 0,
          image: entry.image || '',
        });
      });
    } catch (error) {
      try {
        localStorage.removeItem(this.storageKey);
      } catch (removeError) {
        // rien de plus a faire
      }
    }
  }

  totalQuantity() {
    let total = 0;
    this.items.forEach((item) => {
      total += item.quantity;
    });
    return total;
  }

  currentTier(quantity) {
    let tier = null;
    this.tiers.forEach((candidate) => {
      if (quantity >= candidate.min) tier = candidate;
    });
    return tier;
  }

  nextTier(quantity) {
    return this.tiers.find((candidate) => candidate.min > quantity && candidate.discount > 0) || null;
  }

  /* ---------- render ---------- */

  render() {
    const quantity = this.totalQuantity();
    const tier = this.currentTier(quantity);
    const discountPercent = tier ? tier.discount : 0;

    let subtotal = 0;
    this.items.forEach((item) => {
      subtotal += item.price * item.quantity;
    });
    const discountAmount = Math.round((subtotal * discountPercent) / 100);
    const total = subtotal - discountAmount;
    this.subtotal = subtotal;

    this.schedulePackSave();
    this.renderItems(discountPercent);
    this.renderProgress(quantity, discountPercent);
    this.renderTotals(quantity, subtotal, discountAmount, total, discountPercent);
    this.hideError();
  }

  renderItems(discountPercent) {
    const markup = Array.from(this.items.entries())
      .map(([id, item]) => this.itemMarkup(id, item, discountPercent))
      .join('');

    // Reconstruire les listes detruit l'input en cours de saisie :
    // on note ou etait le curseur pour l'y remettre ensuite.
    const focus = this.captureListFocus();

    if (this.refs.desktopItems) this.refs.desktopItems.innerHTML = markup;
    if (this.refs.mobileItems) this.refs.mobileItems.innerHTML = markup;
    if (this.refs.desktopEmpty) {
      this.refs.desktopEmpty.hidden = this.items.size > 0;
    }
    if (this.refs.desktopItems) {
      this.refs.desktopItems.hidden = this.items.size === 0;
    }

    this.restoreListFocus(focus);

    this.cardRefs.forEach((refs, id) => {
      const item = this.items.get(id);

      if (refs.button) {
        refs.button.hidden = Boolean(item);
        refs.button.setAttribute('aria-pressed', item ? 'true' : 'false');
      }
      if (refs.stepper) refs.stepper.hidden = !item;
      if (refs.badge) {
        refs.badge.hidden = !item;
        if (item) refs.badge.textContent = String(item.quantity);
      }
      if (refs.input && item && document.activeElement !== refs.input) {
        refs.input.value = String(item.quantity);
      }
    });
  }

  captureListFocus() {
    const active = document.activeElement;
    if (!active || !active.matches || !active.matches('[data-mix-match-qty-input]')) return null;

    const list = active.closest('[data-ref="desktop-items-list"], [data-ref="mobile-items-list"]');
    if (!list) return null;

    return {
      listRef: list.dataset.ref,
      variantId: active.dataset.variantId,
      start: active.selectionStart,
      end: active.selectionEnd,
    };
  }

  restoreListFocus(focus) {
    if (!focus) return;

    const list = this.querySelector(`[data-ref="${focus.listRef}"]`);
    const input = list && list.querySelector(`[data-variant-id="${focus.variantId}"]`);
    if (!input) return;

    input.focus();
    try {
      input.setSelectionRange(focus.start, focus.end);
    } catch (error) {
      // type number selon le navigateur : le focus seul suffit
    }
  }

  static escapeHtml(value) {
    return String(value == null ? '' : value).replace(/[&<>"']/g, (char) => {
      switch (char) {
        case '&': return '&amp;';
        case '<': return '&lt;';
        case '>': return '&gt;';
        case '"': return '&quot;';
        default: return '&#39;';
      }
    });
  }

  itemMarkup(id, item, discountPercent) {
    const unitPrice = Math.round((item.price * (100 - discountPercent)) / 100);
    const showCompare = discountPercent > 0 || item.compareAt > item.price;
    const compareValue = discountPercent > 0 ? item.price : item.compareAt;

    return `
      <li class="mix-match-summary__item" data-variant-id="${id}">
        ${
          item.image
            ? `<img src="${MixAndMatch.escapeHtml(item.image)}" alt="" class="mix-match-summary__item-image" width="48" height="48" loading="lazy">`
            : '<span class="mix-match-summary__item-image"></span>'
        }
        <div class="mix-match-summary__item-info">
          <span class="mix-match-summary__item-title">${MixAndMatch.escapeHtml(item.title)}</span>
          <div class="mix-match-summary__item-meta">
            <span class="mix-match-summary__item-price">
              ${showCompare ? `<s class="mix-match-summary__item-compare">${this.formatMoney(compareValue)}</s>` : ''}
              <span class="mix-match-summary__item-price-current">${this.formatMoney(unitPrice)}</span>
            </span>
            <span class="mix-match-qty">
              <button type="button" data-mix-match-step="-1" data-variant-id="${id}" aria-label="${this.settings.decreaseLabel}">&minus;</button>
              <input type="number" value="${item.quantity}" min="0" inputmode="numeric" data-mix-match-qty-input data-variant-id="${id}" aria-label="${this.settings.quantityLabel}">
              <button type="button" data-mix-match-step="1" data-variant-id="${id}" aria-label="${this.settings.increaseLabel}">+</button>
            </span>
          </div>
        </div>
        <button type="button" class="mix-match-summary__item-remove" data-mix-match-remove="${id}" aria-label="${this.settings.removeLabel}">&times;</button>
      </li>
    `;
  }

  renderProgress(quantity, discountPercent) {
    const next = this.nextTier(quantity);

    const meals = quantity * (this.settings.mealsPerProduct || 0);
    const missionText =
      quantity === 0
        ? this.settings.missionEmptyLabel || ''
        : (this.settings.missionProgressLabel || '').replace(
            '[meals]',
            `<strong>${meals.toLocaleString()}</strong>`
          );

    const message = missionText
      ? `<span class="mix-match-tier-progress__message-text">${missionText}</span>`
      : '';

    this.renderUnitPrice(quantity, this.subtotal, discountPercent);

    this.refs.progressMessage.forEach((node) => {
      node.innerHTML = `<p class="mix-match-tier-progress__message">${message}</p>`;
    });

    this.refs.progressSegments.forEach((container) => {
      container.querySelectorAll('[data-tier-min]').forEach((segment) => {
        segment.classList.toggle(
          'mix-match-tier-progress__segment--filled',
          quantity >= Number(segment.dataset.tierMin)
        );
      });
    });

    this.refs.progressRoot.forEach((node) => {
      node.setAttribute('aria-valuenow', String(Math.min(quantity, this.tiers.length)));
    });

    if (this.refs.nextTier) {
      this.refs.nextTier.hidden = !next;
      const nextText = this.refs.nextTier.querySelector('[data-ref="next-tier-text"]');
      if (nextText && next) {
        const missing = next.min - quantity;
        const noun = missing > 1 ? this.settings.productsLabel : this.settings.productLabel;
        nextText.innerHTML = `${this.settings.addMoreLabel.replace(
          '[count]',
          `<strong>${missing} ${noun}</strong>`
        )} <strong>-${next.discount}%</strong>`;
      }
    }
  }

  renderUnitPrice(quantity, subtotal, discountPercent) {
    const nodes = this.unitPriceNodes;
    if (!nodes.length) return;

    const hide = quantity === 0 || subtotal === 0;
    const unitPrice = hide ? 0 : Math.round(subtotal / quantity);

    // Le prix barre ne vaut que pour une remise reellement acquise :
    // annoncer celle du palier suivant afficherait un prix que le client ne paie pas.
    const discounted = Math.round((unitPrice * (100 - discountPercent)) / 100);
    const suffix = this.settings.unitPriceLabel || '';

    nodes.forEach((node) => {
      node.hidden = hide;
      if (node.hidden) return;
      node.innerHTML = `
        <strong class="mix-match-tier-progress__unit-current">${this.formatMoney(discounted)}</strong>
        <span class="mix-match-tier-progress__unit-suffix">${suffix}</span>
        ${
          discountPercent > 0
            ? `<span class="mix-match-tier-progress__unit-off">(-${discountPercent}%)</span>`
            : ''
        }
      `;
    });
  }

  renderTotals(quantity, subtotal, discountAmount, total, discountPercent) {
    const empty = this.items.size === 0;
    const dash = '\u2014';

    if (this.refs.subtotal) {
      this.refs.subtotal.textContent = empty ? dash : this.formatMoney(subtotal);
    }
    if (this.refs.discountRow) this.refs.discountRow.hidden = !empty && discountAmount === 0;
    if (this.refs.discountValue) {
      this.refs.discountValue.textContent = empty
        ? dash
        : `-${this.formatMoney(discountAmount)} (${discountPercent}%)`;
    }

    const minimum = this.settings.minimumQuantity || 1;
    const reachedMinimum = quantity >= minimum;

    this.refs.atcLabels.forEach((label) => {
      label.textContent = this.settings.atcLabel;
    });

    this.refs.atcButtons.forEach((button) => {
      button.setAttribute('aria-disabled', reachedMinimum ? 'false' : 'true');
      button.disabled = !reachedMinimum;
    });

    if (this.refs.toggle) {
      this.refs.toggle.setAttribute('aria-disabled', quantity === 0 ? 'true' : 'false');
    }

    this.refs.count.forEach((node) => {
      node.textContent = String(quantity);
      node.hidden = quantity === 0;
    });

    if (quantity === 0) this.toggleMobilePanel(false);
  }

  /* ---------- mobile panel ---------- */

  toggleMobilePanel(force) {
    if (!this.shell) return;
    if (this.totalQuantity() === 0 && force !== false) return;

    const open = typeof force === 'boolean' ? force : !this.shell.classList.contains('is-open');
    this.shell.classList.toggle('is-open', open);

    if (this.refs.toggle) {
      this.refs.toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      const label = this.refs.toggle.querySelector('[data-ref="mobile-toggle-label"]');
      if (label) label.textContent = open ? this.settings.closeLabel : this.settings.toggleLabel;
    }
  }

  /* ---------- cart ---------- */

  async addToCart() {
    if (this.busy || this.items.size === 0) return;
    const quantity = this.totalQuantity();
    if (quantity < (this.settings.minimumQuantity || 1)) return;

    this.busy = true;
    this.setLoading(true);

    const tierNow = this.currentTier(quantity);
    const codeNow = tierNow && tierNow.code ? tierNow.code : '';

    // Le HTML du drawer pese plus de 300 Ko : on ne le demande que s'il va
    // etre affiche tel quel. Avec un code a poser, il serait deja perime,
    // et en partant au checkout la page est quittee.
    const needsDrawerHtml = !codeNow && !this.settings.goToCheckout;

    const payload = {
      items: Array.from(this.items.entries()).map(([id, item]) => ({
        id: Number(id),
        quantity: item.quantity,
        // Marqueur lu par la discount function : sans lui, aucune remise de palier.
        // Le prefixe _ le garde hors de l'affichage panier et checkout.
        properties: { _pack: '1' },
      })),
      sections: needsDrawerHtml ? 'cart-drawer,cart-icon-bubble' : 'cart-icon-bubble',
      sections_url: window.location.pathname,
    };

    try {
      const response = await fetch(`${this.settings.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(payload),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.description || this.settings.genericError);
      }

      const tier = this.currentTier(quantity);
      const code = tier && tier.code ? tier.code : '';

      // Panier et compteur du header sont rafraichis avant toute redirection,
      // pour que le retour arriere depuis le checkout retrouve un panier a jour.
      if (code) {
        this.appliedCode = code;

        // Vers le checkout : la redirection porte le code.
        if (this.settings.goToCheckout) {
          this.renderCartSections(data.sections);
          this.markHandoff();
          this.clearPack();
          window.location.href = `/discount/${encodeURIComponent(code)}?redirect=${encodeURIComponent(
            this.settings.routes.checkout
          )}`;
          return;
        }

        // Sinon on reste sur place : le code est pose sans navigation,
        // puis le panier est re-rendu avec la remise avant ouverture.
        await fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' });

        const refreshed = await fetch(`${this.settings.routes.cart}/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            updates: {},
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname,
          }),
        }).then((r) => r.json());

        this.clearPack();
        this.renderCartSections(refreshed.sections);
        this.openCartOrRedirect();
        return;
      }

      if (this.settings.goToCheckout) {
        this.renderCartSections(data.sections);
        this.markHandoff();
        this.clearPack();
        window.location.href = this.settings.routes.checkout;
        return;
      }

      this.updateCart(data);
    } catch (error) {
      this.showError(error.message || this.settings.genericError);
      this.busy = false;
      this.setLoading(false);
    }
  }

  clearPack() {
    this.items.clear();
    this.busy = false;
    this.setLoading(false);
    this.render();
    this.savePack();
  }

  /* ---------- passage de relais vers le checkout ---------- */

  markHandoff() {
    try {
      sessionStorage.setItem(`${this.storageKey}:handoff`, '1');
    } catch (error) {
      // sans sessionStorage, le pack sera simplement conserve
    }
  }

  readHandoff() {
    try {
      return sessionStorage.getItem(`${this.storageKey}:handoff`) === '1';
    } catch (error) {
      return false;
    }
  }

  clearHandoff() {
    try {
      sessionStorage.removeItem(`${this.storageKey}:handoff`);
    } catch (error) {
      // rien de plus a faire
    }
  }

  updateCart(data) {
    const drawer = document.querySelector('cart-drawer');

    // Le theme pilote lui-meme son drawer : lui passer la reponse d'ajout
    // le fait rendre son contenu et s'ouvrir, la ou une injection manuelle
    // se fait ecraser.
    if (drawer && typeof drawer.renderContents === 'function') {
      this.busy = false;
      this.setLoading(false);
      this.toggleMobilePanel(false);
      drawer.renderContents(data);
      this.clearPack();
      return;
    }

    this.renderCartSections(data.sections);
    this.openCartOrRedirect();
    this.clearPack();
  }

  renderCartSections(sections) {
    if (!sections) return;

    // Remplacer le HTML recree l'element <cart-drawer> : sans cela il perd
    // sa classe d'ouverture et le panneau se referme sous le client.
    const openDrawer = document.querySelector('cart-drawer.active');

    Object.entries(sections).forEach(([id, html]) => {
      if (typeof html !== 'string') return;
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source =
        doc.querySelector(`#shopify-section-${id}`) || doc.querySelector('.shopify-section') || doc.body;
      const target =
        document.querySelector(`#shopify-section-${id}`) || document.getElementById(id);
      if (source && target) target.innerHTML = source.innerHTML;
    });

    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer.classList.remove('is-empty');
      if (openDrawer) drawer.classList.add('active');
    }
  }

  openCartOrRedirect() {
    const drawer = document.querySelector('cart-drawer');

    this.busy = false;
    this.setLoading(false);
    this.toggleMobilePanel(false);

    // Jamais de navigation vers /cart : le parcours reste dans le drawer.
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
      return;
    }

    if (drawer) drawer.classList.add('active');
  }

  setLoading(loading) {
    this.refs.atcButtons.forEach((button) => {
      button.classList.toggle('loading', loading);
      button.setAttribute('aria-busy', loading ? 'true' : 'false');
    });
  }

  showError(message) {
    this.refs.errors.forEach((node) => {
      node.textContent = message;
      node.classList.remove('hidden');
    });
  }

  hideError() {
    this.refs.errors.forEach((node) => {
      node.textContent = '';
      node.classList.add('hidden');
    });
  }

  /* ---------- money ---------- */

  formatMoney(cents) {
    const format = this.moneyFormat;
    const value = cents / 100;

    const withDecimals = (separator, thousands) => {
      const parts = value.toFixed(2).split('.');
      const integer = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return `${integer}${separator}${parts[1]}`;
    };

    const noDecimals = (thousands) =>
      Math.round(value)
        .toString()
        .replace(/\B(?=(\d{3})+(?!\d))/g, thousands);

    return format.replace(/\{\{\s*(\w+)\s*\}\}/g, (match, token) => {
      switch (token) {
        case 'amount':
          return withDecimals('.', ',');
        case 'amount_no_decimals':
          return noDecimals(',');
        case 'amount_with_comma_separator':
          return withDecimals(',', '.');
        case 'amount_no_decimals_with_comma_separator':
          return noDecimals('.');
        case 'amount_with_space_separator':
          return withDecimals(',', ' ');
        case 'amount_no_decimals_with_space_separator':
          return noDecimals(' ');
        case 'amount_with_apostrophe_separator':
          return withDecimals('.', "'");
        default:
          return withDecimals('.', ',');
      }
    });
  }
}

if (!customElements.get('mix-and-match')) {
  customElements.define('mix-and-match', MixAndMatch);
}
